import { features } from 'constants/featureFlags';
import { TrackingProperty } from 'constants/trackingEvents';
import useFullstory from 'hooks/useFullstory';
import { CommunityContext as CommunityContextInterface } from 'interfaces/community';
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';
import { TrackEvent } from 'types/analytics';
import { Userpilot } from 'userpilot';
import { isTextDark } from 'utils/colors';
import { EVENT_USER_ROLES } from 'utils/constants';
import { initializeFeatureFlags } from 'utils/featureFlags';
import { MAIN_QUERY } from 'utils/queries';
import { rawId } from 'utils/rawId';
import { isAdmin, isModerator, isSiteAdmin } from 'utils/userHelpers';

import React, { ReactNode, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';

// Right now the toga definitions are created automatically
// and they don't cover context providers
// @ts-ignore
import { ThemeContext } from '@thinkific/toga-react';

import FacebookPixelScript from 'components/FacebookPixelScript';
import FontProvider from 'components/FontProvider';
import PageSkeleton from 'components/PageSkeleton';

const THINKIFIC_TRACKER_NAME = 'thinkificGA';
const TENANT_TRACKER_NAME = 'tenantGA';

interface CommunityContextProviderProps {
  children: ReactNode;
  communityId: string;
  spaceId?: string;
}

const CommunityContext = React.createContext({} as CommunityContextInterface);

const useCommunityContext = (): CommunityContextInterface => useContext(CommunityContext);

// initializes thinkific's and tenant's GA (if provided)
export const initializeGoogleAnalytics = (tenantGAKey: string): void => {
  const tenantGA = getTenantGA(tenantGAKey);

  ReactGA.initialize([
    {
      trackingId: process.env.GOOGLE_ANALYTICS_KEY || '',
      gaOptions: { name: THINKIFIC_TRACKER_NAME },
    },
    ...tenantGA,
  ]);

  ReactGA.pageview('communities', [
    THINKIFIC_TRACKER_NAME,
    ...(tenantGAKey ? [TENANT_TRACKER_NAME] : []),
  ]);
};

const initializeGoogleAnalytics4 = (tenantGAMeasurementId: string): void => {
  ReactGA4.initialize(tenantGAMeasurementId);
  ReactGA4.send('pageview');
};

interface TenantGAData {
  trackingId: string;
  gaOptions: { name: string };
}

export const getTenantGA = (tenantGAKey: string): Array<TenantGAData> =>
  tenantGAKey
    ? [
        {
          trackingId: tenantGAKey,
          gaOptions: { name: TENANT_TRACKER_NAME },
        },
      ]
    : [];

interface ThinkificAnalyticsData {
  track(event: string, options: unknown): void;
}

const CommunityProvider = ({ children, communityId, spaceId }: CommunityContextProviderProps) => {
  const [thinkificAnalytics, setThinkificAnalytics] = useState<ThinkificAnalyticsData>();

  const { loading, data } = useQuery(MAIN_QUERY, {
    variables: { communityId },
  });

  const { site, community, me: currentUser } = data || {};

  const mentionableUsers = data?.community?.mentionableUsers;
  const communityUsers = data?.community?.communityUsers;
  const { filestackPolicy: filestackSecurity } = site || {};
  const featureFlags = site && site.featureFlags ? initializeFeatureFlags(site.featureFlags) : {};

  const isDMsAccessible =
    featureFlags[features.DIRECT_MESSAGING] && !!site?.features?.directMessaging?.isEnabled;

  useEffect(() => {
    if (site && process.env.NODE_ENV === 'production') {
      initializeGoogleAnalytics(site.googleAnalyticsKey);
    }
  }, [site && site.googleAnalyticsKey]);

  useEffect(() => {
    if (site?.integrations?.gaMeasurementId && process.env.NODE_ENV === 'production') {
      initializeGoogleAnalytics4(site.integrations.gaMeasurementId);
    }
  }, [site?.integrations?.gaMeasurementId]);

  useEffect(() => {
    const loadAnalytics = async () => {
      // Importing the ThinkificAnalytics library with SSR throws errors
      // which is why this util gets imported dynamically only on the client.
      const getThinkificAnalytics = (await import('utils/analytics')).default;
      const analytics = getThinkificAnalytics(process.env.MIXPANEL_TOKEN, site.trackingKey);

      setThinkificAnalytics(analytics);
    };

    if (site && !thinkificAnalytics && featureFlags[features.STUDENT_EVENT_TRACKING]) {
      if (process.env.MIXPANEL_TOKEN) {
        loadAnalytics();
      } else {
        // eslint-disable-next-line no-console
        console.warn('No MIXPANEL_TOKEN provided. Skipping event tracking');
      }
    }
  }, [site]);

  useEffect(() => {
    if (process.env.USERPILOT_TOKEN) {
      Userpilot.initialize(process.env.USERPILOT_TOKEN);
    } else {
      // eslint-disable-next-line no-console
      console.warn('No USERPILOT_TOKEN provided. Skipping Userpilot initialization.');
    }
  }, []);

  useEffect(() => {
    if (site && community && currentUser && process.env.USERPILOT_TOKEN) {
      if (isModerator(currentUser.permissions) || isAdmin(currentUser)) {
        // Only add DMS_ENABLED property if feature flag and plan conditions are met
        const identifyProperties = {
          [TrackingProperty.USER_NAME]: currentUser.fullName,
          [TrackingProperty.USER_EMAIL]: currentUser.email,
          [TrackingProperty.COMMUNITY_ROLE_TYPE]: currentUser.roles.join(','),
          [TrackingProperty.COMMUNITY_ID]: community.id,
          [TrackingProperty.NUMBER_OF_COMMUNITY_MEMBERS]: communityUsers.totalCount,
          [TrackingProperty.NUMBER_OF_SPACES]:
            community.communitySections.edges[0].node.communitySpaces.totalCount,
        };

        if (isDMsAccessible) {
          const directMessagingEnabled = site?.directMessagingEnabled;
          identifyProperties[TrackingProperty.DMS_ENABLED] = directMessagingEnabled;
        }

        Userpilot.identify(currentUser.gid, identifyProperties);
      }
    }
  }, [site, community, currentUser, featureFlags]);

  useFullstory({ fullstory: site?.fullstory, user: currentUser });

  useEffect(() => {
    if (currentUser && currentUser.locale) {
      document.getElementsByTagName('html')[0].setAttribute('lang', currentUser.locale);
    }
  }, [currentUser]);

  if (loading || !data) {
    return <PageSkeleton />;
  }

  const trackEvent: TrackEvent = (event, eventProperties) => {
    if (!thinkificAnalytics) {
      return null;
    }

    const { SITE_ADMIN, COMMUNITY_MODERATOR, STUDENT } = EVENT_USER_ROLES;
    let userRole;

    if (isSiteAdmin(currentUser.roles)) {
      userRole = SITE_ADMIN;
    } else if (isModerator(currentUser.permissions)) {
      userRole = COMMUNITY_MODERATOR;
    } else {
      userRole = currentUser.roles.join(', ');
    }

    const baseProperties = {
      [TrackingProperty.COMMUNITY_ID]: rawId(communityId),
      [TrackingProperty.COMMUNITY_ID_ENCODED]: communityId,
      [TrackingProperty.COMMUNITY_NAME]: community.name,
      [TrackingProperty.TENANT_ID]: rawId(site.id),
      [TrackingProperty.TENANT_ID_ENCODED]: site.id,
      [TrackingProperty.USER_ID]: rawId(currentUser.id),
      [TrackingProperty.USER_ID_ENCODED]: currentUser.id,
      [TrackingProperty.USER_ROLE]: userRole || STUDENT,
    };

    if (spaceId) {
      baseProperties[TrackingProperty.SPACE_ID] = rawId(spaceId);
      baseProperties[TrackingProperty.SPACE_ID_ENCODED] = spaceId;
    }

    return thinkificAnalytics.track(event, {
      ...baseProperties,
      ...eventProperties,
    });
  };

  const facebookPixelCode = site.integrations.facebookPixelCode || '';
  // Extract the Facebook Pixel ID from the script
  const matchResult = facebookPixelCode.match(/fbq\('init', '([0-9]+)'\)/) || '';
  const facebookPixelId = matchResult.length === 2 ? Number(matchResult[1]) : null;

  const theme = {
    color: {
      // primary: community?.primaryColor, // Disabling until PPG-161 is complete
    },
    button: {
      allCaps: false,
    },
    loader: {
      color: isTextDark(community?.primaryColor) ? 'black' : 'white',
    },
  };

  const NLACommunitiesEnabled =
    ((featureFlags[features.NLA_COMMUNITIES] || site?.features?.newCommunities?.isEnabled) &&
      window.self !== window.top) ||
    false;

  const inLearnerHub =
    featureFlags[features.LEARNER_HUB] &&
    site.newLearnerApplicationEnabled &&
    window?.self !== window?.top;

  return (
    <CommunityContext.Provider
      value={{
        site,
        community,
        communityUsers,
        mentionableUsers,
        spaceId,
        currentUser: {
          ...currentUser,
          isModerator: isModerator(currentUser.permissions),
          isSiteAdmin: isSiteAdmin(currentUser.roles),
        },
        trackEvent,
        filestackSecurity,
        featureFlags,
        inLearnerHub,
        NLACommunitiesEnabled,
      }}
    >
      <ThemeContext.Provider value={theme}>
        {facebookPixelId && <FacebookPixelScript id={facebookPixelId} />}
        <FontProvider font={community.font} />
        {children}
      </ThemeContext.Provider>
    </CommunityContext.Provider>
  );
};

export { CommunityContext, CommunityProvider, useCommunityContext };
